"use client"

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { registerAffiliateAction } from '@/lib/actions/affiliate-auth';
import { AFFILIATE_TYPE_LABELS, COUNTRIES } from '@/lib/affiliates/constants';
import { useRecaptcha } from '@/lib/recaptcha/use-recaptcha';
import type { AffiliateType } from '@/lib/affiliates/types';

export default function AffiliateRegisterPage() {
    const router = useRouter();
    const { getToken } = useRecaptcha();
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        confirmPassword: '',
        first_name: '',
        last_name: '',
        company_name: '',
        phone: '',
        website: '',
        country: 'ES',
        type: 'customer' as AffiliateType,
        referral_source: '',
        tiktok_url: '',
        instagram_url: '',
        twitter_url: '',
        agreed_to_terms: false,
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        // Validations
        if (formData.password !== formData.confirmPassword) {
            setError('Las contraseñas no coinciden');
            setLoading(false);
            return;
        }

        if (formData.password.length < 8) {
            setError('La contraseña debe tener al menos 8 caracteres');
            setLoading(false);
            return;
        }

        if (!formData.agreed_to_terms) {
            setError('Debes aceptar los términos y condiciones');
            setLoading(false);
            return;
        }

        try {
            // Get reCAPTCHA token
            const recaptchaToken = await getToken('affiliate_register');

            const result = await registerAffiliateAction({ ...formData, recaptchaToken });

            if (result.success) {
                setSuccess(true);
            } else {
                setError(result.error || 'Error al registrarse');
            }
        } catch (err) {
            setError('Error al registrarse. Inténtalo de nuevo.');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
                <div className="max-w-md w-full text-center">
                    <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                        <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                        ¡Registro completado!
                    </h2>
                    <p className="text-gray-600 mb-6">
                        Tu solicitud ha sido enviada. Recibirás un email cuando sea aprobada.
                    </p>
                    <Link
                        href="/affiliate-login"
                        className="inline-block px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Ir a iniciar sesión
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
            <div className="max-w-lg w-full space-y-8">
                <div className="text-center">
                    <Link href="/" className="inline-block mx-auto">
                        <img
                            src="/zindra_black.svg"
                            alt="Zindra"
                            className="h-9 mx-auto"
                        />
                    </Link>
                    <h2 className="mt-6 text-3xl font-bold text-gray-900">
                        Únete como Afiliado
                    </h2>
                    <p className="mt-2 text-sm text-gray-600">
                        Gana comisiones recomendando Zindra
                    </p>
                </div>

                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                            {error}
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
                                Nombre *
                            </label>
                            <input
                                id="first_name"
                                name="first_name"
                                type="text"
                                required
                                value={formData.first_name}
                                onChange={handleChange}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>

                        <div>
                            <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
                                Apellidos *
                            </label>
                            <input
                                id="last_name"
                                name="last_name"
                                type="text"
                                required
                                value={formData.last_name}
                                onChange={handleChange}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                            Email *
                        </label>
                        <input
                            id="email"
                            name="email"
                            type="email"
                            required
                            value={formData.email}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                Contraseña *
                            </label>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                required
                                value={formData.password}
                                onChange={handleChange}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Mín. 8 caracteres"
                            />
                        </div>

                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                                Confirmar contraseña *
                            </label>
                            <input
                                id="confirmPassword"
                                name="confirmPassword"
                                type="password"
                                required
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="company_name" className="block text-sm font-medium text-gray-700">
                            Empresa (opcional)
                        </label>
                        <input
                            id="company_name"
                            name="company_name"
                            type="text"
                            value={formData.company_name}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                                Tipo de afiliado *
                            </label>
                            <select
                                id="type"
                                name="type"
                                value={formData.type}
                                onChange={handleChange}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            >
                                {Object.entries(AFFILIATE_TYPE_LABELS).map(([value, labels]) => (
                                    <option key={value} value={value}>
                                        {labels.es}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                                País *
                            </label>
                            <select
                                id="country"
                                name="country"
                                value={formData.country}
                                onChange={handleChange}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            >
                                {COUNTRIES.map((country) => (
                                    <option key={country.code} value={country.code}>
                                        {country.name.es}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div>
                        <label htmlFor="website" className="block text-sm font-medium text-gray-700">
                            Sitio web (opcional)
                        </label>
                        <input
                            id="website"
                            name="website"
                            type="url"
                            value={formData.website}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            placeholder="https://..."
                        />
                    </div>

                    {/* Social Media Section */}
                    <div className="border-t border-gray-200 pt-4">
                        <p className="text-sm font-medium text-gray-700 mb-3">Redes sociales (opcional)</p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label htmlFor="tiktok_url" className="block text-xs font-medium text-gray-600">
                                    TikTok
                                </label>
                                <input
                                    id="tiktok_url"
                                    name="tiktok_url"
                                    type="url"
                                    value={formData.tiktok_url}
                                    onChange={handleChange}
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                                    placeholder="https://tiktok.com/@..."
                                />
                            </div>

                            <div>
                                <label htmlFor="instagram_url" className="block text-xs font-medium text-gray-600">
                                    Instagram
                                </label>
                                <input
                                    id="instagram_url"
                                    name="instagram_url"
                                    type="url"
                                    value={formData.instagram_url}
                                    onChange={handleChange}
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                                    placeholder="https://instagram.com/..."
                                />
                            </div>

                            <div>
                                <label htmlFor="twitter_url" className="block text-xs font-medium text-gray-600">
                                    X (Twitter)
                                </label>
                                <input
                                    id="twitter_url"
                                    name="twitter_url"
                                    type="url"
                                    value={formData.twitter_url}
                                    onChange={handleChange}
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                                    placeholder="https://x.com/..."
                                />
                            </div>
                        </div>
                    </div>

                    <div>
                        <label htmlFor="referral_source" className="block text-sm font-medium text-gray-700">
                            ¿Cómo nos conociste? (opcional)
                        </label>
                        <input
                            id="referral_source"
                            name="referral_source"
                            type="text"
                            value={formData.referral_source}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    <div className="flex items-start">
                        <input
                            id="agreed_to_terms"
                            name="agreed_to_terms"
                            type="checkbox"
                            checked={formData.agreed_to_terms}
                            onChange={handleChange}
                            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="agreed_to_terms" className="ml-2 text-sm text-gray-600">
                            Acepto los{' '}
                            <a href="/legal/affiliate-terms" className="text-blue-600 hover:text-blue-500">
                                términos y condiciones
                            </a>{' '}
                            del programa de afiliados
                        </label>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? 'Registrando...' : 'Registrarme como afiliado'}
                    </button>

                    <div className="text-center text-sm">
                        <span className="text-gray-600">¿Ya tienes cuenta? </span>
                        <Link href="/affiliate-login" className="font-medium text-blue-600 hover:text-blue-500">
                            Iniciar sesión
                        </Link>
                    </div>
                </form>
            </div>
        </div>
    );
}
